import { Button, Flex, Table, type TableColumnType, message } from 'antd';
import { format } from 'date-fns';
import { FC, KeyboardEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminAccountsController_syncConnection } from '@api-client/generated/AdminAccountsController/syncConnection';
import { Schemas } from '@api-client/generated/types';
import { ArchivedTag } from '@components';
import Amount from '@entities/Amount';
import { useCurrentCompanyId } from '@hooks';
import { captureException } from '@sentry/react';

import AccountReconciliation from './AccountReconciliation';
import * as S from './styled';

type Account = Schemas.Account;

type AccountsListProps = {
  accounts: Account[];
};

/** The bank's own figure when it sends one, otherwise whatever balance we hold. */
const getBalance = (account: Account): Schemas.Balance | undefined =>
  account.balances?.find((balance) => balance.balanceType === 'calculated') ||
  account.balances?.[0];

const getAccountLabel = (account: Account): string =>
  account.accountName || account.name || account.accountNumber || '—';

const AccountsList: FC<AccountsListProps> = ({ accounts }) => {
  const companyId = useCurrentCompanyId();
  const navigate = useNavigate();

  // Sync is fire-and-forget on the backend, so "Requested" is UI-only state that lives
  // until the page is reloaded. Kept per account id, since one table now holds them all.
  const [requestedIds, setRequestedIds] = useState<string[]>([]);

  const { mutate: syncAccount } = useAdminAccountsController_syncConnection();

  const processSync = (account: Account) => {
    setRequestedIds((ids) => [...ids, account.id]);

    syncAccount(
      { parameter: { id: account.id, companyId } },
      {
        onError: (error) => {
          // Hand the button back instead of leaving the row stuck on "Requested" forever.
          setRequestedIds((ids) => ids.filter((id) => id !== account.id));
          message.error('Failed to request a sync. Please try again.');
          captureException(error, {
            level: 'error',
            tags: { feature: 'accounts-sync' },
          });
        },
      }
    );
  };

  const columns: TableColumnType<Account>[] = [
    {
      key: 'name',
      title: 'Account',
      ellipsis: true,
      render: (_, account) => (
        <Flex align="center" gap={8}>
          <S.Name>{getAccountLabel(account)}</S.Name>
          {account.isArchived && <ArchivedTag />}
        </Flex>
      ),
    },
    {
      key: 'balance',
      title: 'Balance',
      align: 'right',
      width: 160,
      render: (_, account) => {
        const balance = getBalance(account);

        if (!balance) {
          return '—';
        }

        return (
          <Amount
            amount={Number(balance.balanceAmount.amount)}
            currencyCode={balance.balanceAmount.currency}
            withoutType
          />
        );
      },
    },
    {
      key: 'currencyCode',
      dataIndex: 'currencyCode',
      title: 'Currency',
      width: 110,
      render: (currencyCode: string) => currencyCode || '—',
    },
    {
      key: 'lastSyncedAt',
      dataIndex: 'lastSyncedAt',
      title: 'Last synced',
      width: 160,
      render: (lastSyncedAt: string | null) =>
        lastSyncedAt ? format(new Date(lastSyncedAt), 'dd MMM yyyy') : '—',
    },
    {
      key: 'reconciliation',
      title: 'Reconciled',
      width: 170,
      render: (_, account) => (
        <AccountReconciliation
          reconciliation={account.reconciliation}
          currencyCode={account.currencyCode}
        />
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 140,
      align: 'right',
      render: (_, account) => {
        // Only bank-connected accounts can be synced: the backend resolves the account's
        // connection to sync it, so asking for a CSV-only account is a guaranteed error.
        if (!account.connectionId) {
          return null;
        }

        const requested = requestedIds.includes(account.id);

        return (
          <Button
            type="text"
            disabled={requested}
            onClick={(event) => {
              // The row itself navigates to the account's transactions.
              event.stopPropagation();
              processSync(account);
            }}
          >
            {requested ? 'Requested' : 'Sync now'}
          </Button>
        );
      },
    },
  ];

  return (
    <S.TableContainer>
      <Table<Account>
        rowKey={({ id }) => id}
        dataSource={accounts}
        columns={columns}
        pagination={false}
        // `TransactionsContent` seeds its filters from the query string and keys the Bank
        // select on `accountId`, so the transactions page opens already filtered.
        // Rows are clickable, so they are focusable and activatable by keyboard too
        // (same shape as AccessMapTable's rowActivation).
        onRow={(account) => {
          const openTransactions = () =>
            navigate(
              `/companies/${companyId}/transactions?accountId=${account.id}`
            );

          return {
            onClick: openTransactions,
            onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
              // Enter on the Sync button bubbles up to the row; only the row's own
              // key presses should navigate.
              if (event.target !== event.currentTarget) {
                return;
              }

              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openTransactions();
              }
            },
            tabIndex: 0,
          };
        }}
        scroll={{ x: 720 }}
      />
    </S.TableContainer>
  );
};

export default AccountsList;
